import React from 'react';
import { useNavigate } from 'react-router-dom';

const CampaignList = () => {
  const navigate = useNavigate();

  // Mock campaigns with different "death scenarios"
  const campaigns = [
    {
      id: 'FB_001',
      name: 'Black Friday Sale',
      platform: 'Facebook',
      status: 'DEAD',
      deathCause: 'Budget Overdose',
      lastActive: '2025-01-15',
      totalLoss: '$2,847',
      severity: 'critical',
      description: 'Budget increased 5x, performance collapsed'
    },
    {
      id: 'GOOG_234',
      name: 'Holiday Collection',
      platform: 'Google Ads',
      status: 'DYING',
      deathCause: 'Audience Poisoning',
      lastActive: '2025-01-20',
      totalLoss: '$1,235',
      severity: 'high',
      description: 'Expanded to 18-65 age range, quality plummeted'
    },
    {
      id: 'FB_089',
      name: 'New Year Promo',
      platform: 'Facebook',
      status: 'MURDERED',
      deathCause: 'Creative Assassination',
      lastActive: '2025-01-12',
      totalLoss: '$945',
      severity: 'medium',
      description: 'Wrong creative killed conversion rate'
    },
    {
      id: 'GOOG_567',
      name: 'Valentine Special',
      platform: 'Google Ads',
      status: 'CRITICAL',
      deathCause: 'Competitor Attack',
      lastActive: '2025-01-22',
      totalLoss: '$3,120',
      severity: 'critical',
      description: 'Competitors copied strategy and outbid us'
    },
    {
      id: 'FB_445',
      name: 'Spring Launch',
      platform: 'Facebook',
      status: 'BLEEDING',
      deathCause: 'Micro-Waste Leak',
      lastActive: '2025-01-18',
      totalLoss: '$567',
      severity: 'low',
      description: 'Multiple small budget leaks draining performance'
    },
    {
      id: 'GOOG_123',
      name: 'Q1 Growth Campaign',
      platform: 'Google Ads',
      status: 'FLATLINED',
      deathCause: 'Decision Chaos',
      lastActive: '2025-01-10',
      totalLoss: '$4,230',
      severity: 'critical',
      description: 'Too many people making conflicting changes'
    }
  ];

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'critical': return '#ff4444';
      case 'high': return '#ff8800';
      case 'medium': return '#ffaa00';
      case 'low': return '#44ff88';
      default: return '#888';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'DEAD': return '💀';
      case 'DYING': return '🩸';
      case 'MURDERED': return '🔪';
      case 'CRITICAL': return '🚨';
      case 'BLEEDING': return '🩹';
      case 'FLATLINED': return '📉';
      default: return '❓';
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button onClick={() => navigate('/')} style={styles.backButton}>
          ← Back to Dashboard
        </button>
        <h1>🔍 Campaign Detective</h1>
        <p>Your campaigns that need forensic investigation</p>
      </div>

      <div style={styles.grid}>
        {campaigns.map(campaign => (
          <div 
            key={campaign.id} 
            style={{
              ...styles.campaignCard,
              borderColor: getSeverityColor(campaign.severity)
            }}
            onClick={() => navigate(`/timeline/${campaign.id}`)}
          >
            <div style={styles.cardHeader}>
              <span style={styles.statusIcon}>{getStatusIcon(campaign.status)}</span>
              <div>
                <h3 style={styles.campaignName}>{campaign.name}</h3>
                <span style={styles.platform}>{campaign.platform}</span>
              </div>
              <span style={{
                ...styles.status,
                color: getSeverityColor(campaign.severity)
              }}>
                {campaign.status}
              </span>
            </div>

            <div style={styles.deathCause}>
              <strong>Cause of Death:</strong> {campaign.deathCause}
            </div>

            <div style={styles.description}>
              {campaign.description}
            </div>

            <div style={styles.cardFooter}>
              <span style={styles.lastActive}>
                Last Active: {campaign.lastActive}
              </span>
              <span style={styles.loss}>
                Total Loss: <span style={{color: '#ff4444'}}>{campaign.totalLoss}</span>
              </span>
            </div>

            <div style={styles.investigateButton}>
              🕵️ Investigate Timeline →
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '2rem',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '2rem',
  },
  backButton: {
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '1.5rem',
  },
  campaignCard: {
    background: 'rgba(255, 255, 255, 0.05)',
    border: '2px solid',
    borderRadius: '12px',
    padding: '1.5rem',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    position: 'relative',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '1rem',
  },
  statusIcon: {
    fontSize: '1.5rem',
    marginRight: '0.5rem',
  },
  campaignName: {
    margin: 0,
    fontSize: '1.1rem',
  },
  platform: {
    fontSize: '0.8rem',
    color: '#888',
  },
  status: {
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  deathCause: {
    background: 'rgba(255, 68, 68, 0.1)',
    padding: '0.5rem',
    borderRadius: '6px',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  },
  description: {
    color: '#ccc',
    fontSize: '0.9rem',
    marginBottom: '1rem',
    lineHeight: '1.4',
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.8rem',
    color: '#888',
    marginBottom: '1rem',
  },
  lastActive: {},
  loss: {},
  investigateButton: {
    textAlign: 'center',
    background: 'linear-gradient(45deg, #ff6b6b, #ee5a52)',
    color: 'white',
    padding: '0.5rem',
    borderRadius: '6px',
    fontSize: '0.9rem',
    fontWeight: 'bold',
  },
};

export default CampaignList;